import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { SERVICE_ICONS } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ServiceIcon } from "@/components/ServiceIcon";
import { ImageUploader } from "@/components/crm/admin/ImageUploader";
import { mediaAbs } from "@/lib/media";
import { frameStyle } from "@/lib/framing";

const L = ({ children }) => <Label className="text-xs font-semibold text-slate-600">{children}</Label>;

export const ServiceDialog = ({ service, categories = [], onClose, onSaved }) => {
  const isNew = service && !service.id;
  const [v, setV] = useState({});
  const [presets, setPresets] = useState({});
  const [error, setError] = useState("");
  useEffect(() => { api.get("/public/assets").then((r) => setPresets(r.data.presets || {})); }, []);
  useEffect(() => {
    if (service) setV({ name: service.name || "", description: service.description || "", duration_min: service.duration_min ?? 30, price: service.price ?? 0,
      image_url: service.image_url || "", image_framing: service.image_framing || null, category_id: service.category_id || "", icon: service.icon || "Stethoscope", active: service.active ?? true, order: service.order ?? 10 });
    setError("");
  }, [service]);
  const set = (k) => (val) => setV((s) => ({ ...s, [k]: val }));
  const setImage = (url, fr = null) => setV((s) => ({ ...s, image_url: url, image_framing: fr }));
  const card = (f) => (
    <article className="overflow-hidden rounded-xl border border-slate-200 bg-white text-left">
      <div className="aspect-[16/10] overflow-hidden bg-[#F5F7FA]"><img src={mediaAbs(v.image_url)} alt="" className="h-full w-full object-cover" style={frameStyle(f)} /></div>
      <div className="p-2.5"><p className="truncate font-display text-sm font-bold text-[#0B2545]">{v.name || "Servicio"}</p><p className="line-clamp-1 text-xs text-slate-500">{v.description}</p></div>
    </article>
  );
  const valid = v.name?.trim() && v.category_id && Number(v.duration_min) > 0 && Number(v.price) >= 0;

  const save = async () => {
    const body = { ...v, duration_min: Number(v.duration_min), price: Number(v.price), order: Number(v.order) };
    try {
      if (isNew) await api.post("/admin/services", body); else await api.patch(`/admin/services/${service.id}`, body);
      toast.success("Servicio guardado"); onSaved(); onClose();
    } catch (e) { setError(errorMessage(e)); }
  };

  return (
    <Dialog open={!!service} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-2xl sm:max-w-lg" data-testid="service-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl font-extrabold">{isNew ? "Nuevo servicio" : "Editar servicio"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2"><L>Nombre</L><Input value={v.name || ""} onChange={(e) => set("name")(e.target.value)} className="mt-1.5 rounded-xl" data-testid="service-input-name" /></div>
          <div className="sm:col-span-2"><L>Descripción corta</L><Textarea rows={2} value={v.description || ""} onChange={(e) => set("description")(e.target.value)} className="mt-1.5 rounded-xl" data-testid="service-input-description" /></div>
          <div className="sm:col-span-2">
            <L>Categoría *</L>
            <Select value={v.category_id || undefined} onValueChange={set("category_id")}>
              <SelectTrigger className={`mt-1.5 rounded-xl ${v.category_id ? "" : "border-amber-300"}`} data-testid="service-category-select"><SelectValue placeholder="Sin categoría · elige una" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`service-category-option-${c.slug}`}>{c.name}{c.active ? "" : " (inactiva)"}</SelectItem>)}</SelectContent>
            </Select>
            {!v.category_id && <p className="mt-1 text-xs text-amber-700" data-testid="service-category-required">Elige una categoría para guardar.</p>}
          </div>
          <div><L>Duración (min)</L><Input type="number" min="1" value={v.duration_min} onChange={(e) => set("duration_min")(e.target.value)} className="mt-1.5 rounded-xl" data-testid="service-input-duration" /></div>
          <div><L>Precio ($)</L><Input type="number" min="0" step="0.01" value={v.price} onChange={(e) => set("price")(e.target.value)} className="mt-1.5 rounded-xl" data-testid="service-input-price" /></div>
          <div>
            <L>Ícono</L>
            <Select value={v.icon} onValueChange={set("icon")}>
              <SelectTrigger className="mt-1.5 rounded-xl" data-testid="service-icon-select"><SelectValue /></SelectTrigger>
              <SelectContent>{SERVICE_ICONS.map((i) => <SelectItem key={i} value={i}><span className="flex items-center gap-2"><ServiceIcon name={i} className="h-4 w-4" /> {i}</span></SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><L>Orden</L><Input type="number" value={v.order} onChange={(e) => set("order")(e.target.value)} className="mt-1.5 rounded-xl" data-testid="service-input-order" /></div>
          <div className="sm:col-span-2">
            <L>Imagen</L>
            <div className="mt-1.5"><ImageUploader value={v.image_url} onChange={setImage} testid="service-image-upload"
              framing={{ card: v.image_framing }} onFramingChange={(d) => set("image_framing")(d.card)}
              targets={[{ key: "card", label: "Tarjeta", aspect: "16/10" }]} renderCard={card}
              previews={[{ label: "Tarjeta en el CRM", aspect: "16/7", target: "card" }]} /></div>
            <p className="mt-3 text-xs font-semibold text-slate-500">O elige de la galería / pega una URL</p>
            <div className="mt-1.5 grid grid-cols-4 gap-2" data-testid="service-image-presets">
              {Object.entries(presets).map(([k, url]) => (
                <button key={k} type="button" onClick={() => setImage(url)} title={k}
                        className={`aspect-square overflow-hidden rounded-lg ring-2 transition-shadow ${v.image_url === url ? "ring-[#1E6FD9]" : "ring-transparent"}`}
                        data-testid={`service-image-preset-${k}`}>
                  <img src={url} alt={k} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
            <Input value={v.image_url?.startsWith("/api/") ? "" : v.image_url || ""} onChange={(e) => setImage(e.target.value)} placeholder="https://…" className="mt-2 rounded-xl" data-testid="service-input-image-url" />
          </div>
          <label className="flex items-center justify-between rounded-xl bg-[#F5F7FA] p-3 text-sm font-medium sm:col-span-2">
            Activo (visible en el sitio) <Switch checked={v.active} onCheckedChange={set("active")} data-testid="service-active-switch" />
          </label>
          {error && <p className="text-sm text-rose-600 sm:col-span-2" data-testid="service-dialog-error">{error}</p>}
          <Button onClick={save} disabled={!valid} className="rounded-xl bg-[#1E6FD9] sm:col-span-2" data-testid="service-save-button">Guardar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
